import {
  getOrderDeliveryDate,
  getOrderDispatchDate,
  normalizeOrderDate,
} from './orderDateUtils';
import { getPrintingOrderCompany, getPrintingOrderShippingMethod } from './printingToolkitFilterOptions';
import { buildWavePickList, getWaveOrderKey } from './wavePickListModel';

const clean = (value, fallback) => String(value || '').trim() || fallback;

const orderKey = (order) =>
  String(order?.source_id || order?.sourceId || order?.orderId || order?.id || '')
    .trim()
    .replace(/^#/, '');

export function expandCopiesByOrderAliases(orders, copiesByOrder = {}) {
  return (orders || []).reduce((expanded, order) => {
    const identifiers = [
      order?.source_id,
      order?.sourceId,
      order?.orderId,
      order?.id,
    ]
      .map((value) => String(value || '').trim().replace(/^#/, ''))
      .filter(Boolean);
    const copies = identifiers
      .map((identifier) => copiesByOrder[identifier])
      .find((value) => value != null);
    const copiesCount = Math.max(1, parseInt(copies, 10) || 1);
    identifiers.forEach((identifier) => {
      expanded[identifier] = copiesCount;
    });
    return expanded;
  }, { ...copiesByOrder });
}

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

function normalizeDate(value) {
  return normalizeOrderDate(String(value || '')) || clean(value, 'Not specified');
}

function formatDate(value) {
  if (!value || value === 'Not specified') return 'Not specified';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
}

export function getPrintOrderGroupMetadata(order) {
  return {
    shippingMethod: clean(getPrintingOrderShippingMethod(order), 'Unspecified'),
    dispatchDate: normalizeDate(getOrderDispatchDate(order)),
    deliveryDate: normalizeDate(getOrderDeliveryDate(order)),
  };
}

export function analyzePrintSeparatorGroups(orders) {
  const rows = (orders || []).map(getPrintOrderGroupMetadata);
  const distinct = {
    shippingMethod: new Set(rows.map((row) => row.shippingMethod)).size,
    dispatchDate: new Set(rows.map((row) => row.dispatchDate)).size,
    deliveryDate: new Set(rows.map((row) => row.deliveryDate)).size,
  };
  const varyingFields = Object.entries(distinct)
    .filter(([, count]) => count > 1)
    .map(([field]) => field);
  return {
    distinct,
    varyingFields,
    hasMultiple: varyingFields.length > 0,
  };
}

export function groupOrdersForPrintSeparators(orders) {
  const groups = new Map();
  (orders || []).forEach((order) => {
    const metadata = getPrintOrderGroupMetadata(order);
    const key = [
      metadata.shippingMethod,
      metadata.dispatchDate,
      metadata.deliveryDate,
    ].join('::');
    if (!groups.has(key)) groups.set(key, { ...metadata, orders: [] });
    groups.get(key).orders.push(order);
  });
  return Array.from(groups.values()).sort((a, b) =>
    [
      a.shippingMethod.localeCompare(b.shippingMethod),
      a.dispatchDate.localeCompare(b.dispatchDate),
      a.deliveryDate.localeCompare(b.deliveryDate),
    ].find((comparison) => comparison !== 0) || 0,
  );
}

export function groupItemsForPrintSeparators(items, getOrder = (item) => item?.order) {
  const groups = new Map();
  (items || []).forEach((item) => {
    const order = getOrder(item);
    if (!order) return;
    const metadata = getPrintOrderGroupMetadata(order);
    const key = [
      metadata.shippingMethod,
      metadata.dispatchDate,
      metadata.deliveryDate,
    ].join('::');
    if (!groups.has(key)) groups.set(key, { ...metadata, orders: [], items: [] });
    const group = groups.get(key);
    if (!group.orders.some((existing) => orderKey(existing) === orderKey(order))) {
      group.orders.push(order);
    }
    group.items.push(item);
  });
  return Array.from(groups.values()).sort((a, b) =>
    [
      a.shippingMethod.localeCompare(b.shippingMethod),
      a.dispatchDate.localeCompare(b.dispatchDate),
      a.deliveryDate.localeCompare(b.deliveryDate),
    ].find((comparison) => comparison !== 0) || 0,
  );
}

export function countPrintGroupLabels(orders, copiesByOrder = {}) {
  return (orders || []).reduce((total, order) => {
    const copies = Math.max(1, parseInt(copiesByOrder[orderKey(order)], 10) || 1);
    return total + copies;
  }, 0);
}

const MAX_ORDER_ROWS = 22;
const MAX_SKU_ROWS = 26;

/**
 * A4 cover that prints after each shipping-segment label set (lands on top face-up).
 * Reads "Start of …" with order count, shipping methods, and a compact pick list.
 */
export function buildPrintSeparatorHtml(group, options = {}) {
  const formatLabel = clean(options.formatLabel, 'Standard A4');
  const operator = clean(options.operator, 'Current operator');
  const orders = Array.isArray(group?.orders) ? group.orders.filter(Boolean) : [];
  const orderCount = Math.max(
    0,
    Number(options.orderCount) || orders.length || 0,
  );
  const labelCount = Math.max(0, Number(options.labelCount) || orderCount);
  const physicalSheetCount = Math.max(
    0,
    Number(options.physicalSheetCount) || labelCount,
  );
  const printedAt = options.printedAt instanceof Date ? options.printedAt : new Date();
  const printTime = printedAt.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const segmentName = clean(
    options.segmentLabel || group?.shippingMethod,
    'this segment',
  );

  const pick = buildWavePickList(orders, {
    folderLabel: segmentName,
    generatedAt: printedAt.toISOString(),
  });

  const shippingMethods =
    pick.shippingMethods?.length > 0
      ? pick.shippingMethods
      : [{ method: segmentName, orderCount }];

  const methodsLine = shippingMethods
    .map((entry) =>
      shippingMethods.length === 1
        ? entry.method
        : `${entry.method} (${entry.orderCount})`,
    )
    .join(', ');

  const orderRows = orders.map((order) => {
    const key = getWaveOrderKey(order) || orderKey(order) || '—';
    const company = getPrintingOrderCompany(order) || '—';
    const items = Array.isArray(order?.order_items)
      ? order.order_items
      : Array.isArray(order?.items)
        ? order.items
        : Array.isArray(order?.originalItems)
          ? order.originalItems
          : [];
    const qty = items.reduce((sum, item) => {
      const parsed = Number(item?.quantity ?? item?.qty ?? item?.amount);
      return sum + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
    }, 0);
    return { key, company, lineCount: items.length, qty };
  });

  const shownOrders = orderRows.slice(0, MAX_ORDER_ROWS);
  const hiddenOrders = Math.max(0, orderRows.length - shownOrders.length);
  const shownSkus = (pick.lines || []).slice(0, MAX_SKU_ROWS);
  const hiddenSkus = Math.max(0, (pick.lines || []).length - shownSkus.length);

  const dense = orderRows.length > 12 || (pick.lines || []).length > 14;
  const bodySize = dense ? '9.5pt' : '11pt';
  const tableSize = dense ? '8.5pt' : '10pt';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Start of ${escapeHtml(segmentName)}</title>
  <style>
    @page { size: A4 portrait; margin: 0; }
    * { box-sizing: border-box; }
    html, body { width: 210mm; height: 297mm; margin: 0; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #172220;
      background: #fff;
      font-size: ${bodySize};
    }
    .separator-page {
      width: 210mm;
      height: 297mm;
      padding: 12mm 14mm 10mm;
      display: flex;
      flex-direction: column;
      border: 4mm solid #172220;
    }
    .brand {
      margin: 0;
      color: #8c6828;
      font-size: 11pt;
      font-weight: 800;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    h1 {
      margin: 6mm 0 2mm;
      font-size: ${dense ? '22pt' : '28pt'};
      line-height: 1.05;
      text-transform: uppercase;
    }
    .lede {
      margin: 0 0 5mm;
      font-size: ${dense ? '12pt' : '14pt'};
      font-weight: 700;
    }
    .meta {
      display: grid;
      grid-template-columns: 42mm 1fr;
      margin: 0 0 5mm;
      border-top: 0.5mm solid #172220;
    }
    .meta dt, .meta dd {
      margin: 0;
      padding: 2.2mm 1.5mm;
      border-bottom: 0.25mm solid #adb6b3;
      font-size: ${tableSize};
    }
    .meta dt { color: #596561; font-weight: 700; }
    .meta dd { font-weight: 800; }
    .columns {
      display: grid;
      grid-template-columns: 1fr 1.15fr;
      gap: 4mm;
      min-height: 0;
      flex: 1 1 auto;
    }
    h2 {
      margin: 0 0 2mm;
      font-size: 11pt;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: ${tableSize};
    }
    th, td {
      border-bottom: 0.2mm solid #c5cbc8;
      padding: 1.4mm 1mm;
      text-align: left;
      vertical-align: top;
    }
    th {
      color: #596561;
      font-weight: 700;
      border-bottom: 0.35mm solid #172220;
    }
    td.num, th.num { text-align: right; white-space: nowrap; }
    .more {
      margin: 1.5mm 0 0;
      color: #596561;
      font-size: 8.5pt;
      font-weight: 600;
    }
    .footer {
      margin-top: 4mm;
      padding-top: 3mm;
      border-top: 0.5mm solid #172220;
      font-size: 9pt;
      color: #596561;
    }
  </style>
</head>
<body>
  <main class="separator-page">
    <p class="brand">London Smoke &amp; Cure</p>
    <h1>Start of ${escapeHtml(segmentName)}</h1>
    <p class="lede">
      ${orderCount} order${orderCount === 1 ? '' : 's'}
      · ${labelCount} label${labelCount === 1 ? '' : 's'}
      · pickwave for this shipping segment
    </p>
    <dl class="meta">
      <dt>Shipping method${shippingMethods.length === 1 ? '' : 's'}</dt>
      <dd>${escapeHtml(methodsLine)}</dd>
      <dt>Dispatch</dt><dd>${escapeHtml(formatDate(group?.dispatchDate))}</dd>
      <dt>Delivery</dt><dd>${escapeHtml(formatDate(group?.deliveryDate))}</dd>
      <dt>Format</dt><dd>${escapeHtml(formatLabel)}</dd>
      <dt>Physical sheets</dt><dd>${physicalSheetCount}</dd>
      <dt>Printed by</dt><dd>${escapeHtml(operator)}</dd>
      <dt>Prepared</dt><dd>${escapeHtml(printTime)}</dd>
    </dl>
    <div class="columns">
      <section>
        <h2>Orders in this wave</h2>
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Company</th>
              <th class="num">Lines</th>
              <th class="num">Qty</th>
            </tr>
          </thead>
          <tbody>
            ${
              shownOrders.length
                ? shownOrders
                    .map(
                      (row) => `<tr>
              <td>${escapeHtml(row.key)}</td>
              <td>${escapeHtml(row.company)}</td>
              <td class="num">${row.lineCount}</td>
              <td class="num">${row.qty}</td>
            </tr>`,
                    )
                    .join('')
                : '<tr><td colspan="4">No orders in this segment.</td></tr>'
            }
          </tbody>
        </table>
        ${
          hiddenOrders
            ? `<p class="more">...and ${hiddenOrders} more order${
                hiddenOrders === 1 ? '' : 's'
              } on the slips below.</p>`
            : ''
        }
      </section>
      <section>
        <h2>Pick list (this segment)</h2>
        <table>
          <thead>
            <tr>
              <th>SKU / product</th>
              <th class="num">Qty</th>
              <th class="num">Orders</th>
            </tr>
          </thead>
          <tbody>
            ${
              shownSkus.length
                ? shownSkus
                    .map(
                      (line) => `<tr>
              <td>${escapeHtml(line.name || line.sku)}${
                        line.packSize
                          ? ` <span style="color:#596561">(${escapeHtml(
                              line.packSize,
                            )})</span>`
                          : ''
                      }</td>
              <td class="num">${line.quantity}</td>
              <td class="num">${line.orderCount}</td>
            </tr>`,
                    )
                    .join('')
                : '<tr><td colspan="3">No SKU lines for this segment.</td></tr>'
            }
          </tbody>
        </table>
        ${
          hiddenSkus
            ? `<p class="more">...and ${hiddenSkus} more SKU line${
                hiddenSkus === 1 ? '' : 's'
              } - see the full wave pick list if queued.</p>`
            : ''
        }
      </section>
    </div>
    <p class="footer">
      Separator prints after the labels for this shipping segment so it lands on top of that set.
      Start of ${escapeHtml(segmentName)} — walk the pick list, then pack the slips under this sheet.
    </p>
  </main>
</body>
</html>`;
}
